import React, { useMemo } from 'react'
import { Select, SelectContent, SelectItem, SelectScrollDownButton, SelectScrollUpButton, SelectTrigger, SelectValue } from '@shopflo/ui'
import DatePicker from 'src/components/common/calendar/DatePicker'
import { Schedule } from 'src/components/common/export-component/types'
import { generateCronExpression, parseCronExpression } from 'src/components/common/export-component/steps/two/recurring'
import { ordinalSuffix } from 'src/lib/utils/helpers'

const DELIVER_EVERY = [
  { value: 'daily', label: 'Day' },
  { value: 'weekly', label: 'Week' },
  { value: 'monthly', label: 'Month' }
]

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`
}))

// generateCronExpression maps day (1-based) into ['MON'..'SUN'], so Monday = 1.
const DAYS_OF_WEEK = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' }
]

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}${ordinalSuffix(i + 1)}`
}))

const scheduleNote: Record<Schedule['frequency'], string> = {
  daily: 'Exports data for the previous calendar day.',
  weekly: 'Exports data for the last 7 days.',
  monthly: 'Exports data for the last 31 days.',
  hourly: 'Exports data for the previous hour.'
}

/** The "Exports data for …" note for the current schedule — rendered in the dialog footer. */
export const getScheduleNote = (schedule?: Schedule): string => {
  const frequency = schedule?.cronExpression
    ? parseCronExpression(schedule.cronExpression.replace('?', '*')).frequency
    : schedule?.frequency ?? 'daily'
  return scheduleNote[frequency]
}

const fieldLabel = 'text-text-xs-medium text-zinc-500'
const triggerClass = 'h-9 w-full gap-1 rounded-lg bg-white'

interface RecurringScheduleFieldsProps {
  schedule?: Schedule
  onChange: (schedule: Schedule) => void
}

const RecurringScheduleFields: React.FC<RecurringScheduleFieldsProps> = ({ schedule, onChange }) => {
  const parsed = useMemo(() => {
    if (schedule?.cronExpression) return parseCronExpression(schedule.cronExpression.replace('?', '*'))
    return { frequency: schedule?.frequency || 'daily', hour: schedule?.hour ?? 0, day: schedule?.day ?? 1 }
  }, [schedule])

  const frequency = parsed.frequency
  const hour = parsed.hour
  const day = parsed.day ?? 1
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const savedStart = schedule?.scheduledStartDate ? new Date(schedule.scheduledStartDate) : null
  const startDate =
    savedStart && !isNaN(savedStart.getTime()) && savedStart.getTime() >= startOfToday.getTime()
      ? savedStart.toISOString()
      : new Date().toISOString()

  // Labels shift with the delivery cadence: Week → "On", Month → "Date"/"Time".
  const timeLabel = frequency === 'monthly' ? 'Time' : 'At'

  const emit = (next: Partial<{ frequency: Schedule['frequency']; hour: number; day: number; startDate: string }>) => {
    const nextFrequency = next.frequency ?? frequency
    const nextHour = next.hour ?? hour
    const frequencyChanged = next.frequency !== undefined && next.frequency !== frequency
    const nextDay = frequencyChanged ? 1 : next.day ?? day
    const nextStart = next.startDate ?? startDate
    onChange({
      frequency: nextFrequency,
      hour: nextHour,
      day: nextFrequency !== 'daily' ? nextDay : undefined,
      scheduledStartDate: nextStart,
      cronExpression: generateCronExpression({ frequency: nextFrequency, hour: nextHour, day: nextDay })
    })
  }

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex min-w-0 items-end gap-2'>
        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          <label className={fieldLabel}>Deliver every</label>
          <Select value={frequency} onValueChange={value => emit({ frequency: value as Schedule['frequency'] })}>
            <SelectTrigger className={triggerClass}>
              <SelectValue placeholder='Select' />
            </SelectTrigger>
            <SelectContent>
              {DELIVER_EVERY.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {frequency === 'weekly' && (
          <div className='flex min-w-0 flex-1 flex-col gap-1'>
            <label className={fieldLabel}>On</label>
            <Select value={day.toString()} onValueChange={value => emit({ day: Number(value) })}>
              <SelectTrigger className={triggerClass}>
                <SelectValue placeholder='Day' />
              </SelectTrigger>
              <SelectContent className='ui-max-h-52'>
                <SelectScrollUpButton />
                {DAYS_OF_WEEK.map(option => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
                <SelectScrollDownButton />
              </SelectContent>
            </Select>
          </div>
        )}

        {frequency === 'monthly' && (
          <div className='flex min-w-0 flex-1 flex-col gap-1'>
            <label className={fieldLabel}>Date</label>
            <Select value={day.toString()} onValueChange={value => emit({ day: Number(value) })}>
              <SelectTrigger className={triggerClass}>
                <SelectValue placeholder='Date' />
              </SelectTrigger>
              <SelectContent className='ui-max-h-52'>
                <SelectScrollUpButton />
                {DAYS_OF_MONTH.map(option => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
                <SelectScrollDownButton />
              </SelectContent>
            </Select>
          </div>
        )}

        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          <label className={fieldLabel}>{timeLabel}</label>
          <Select value={hour.toString()} onValueChange={value => emit({ hour: Number(value) })}>
            <SelectTrigger className={triggerClass}>
              <SelectValue placeholder='Time' />
            </SelectTrigger>
            <SelectContent className='ui-max-h-52'>
              <SelectScrollUpButton />
              {HOURS.map(option => (
                <SelectItem key={option.value} value={option.value.toString()}>
                  {option.label}
                </SelectItem>
              ))}
              <SelectScrollDownButton />
            </SelectContent>
          </Select>
        </div>

        <div className='flex w-[274px] shrink-0 flex-col gap-1'>
          <label className={fieldLabel}>Starts on</label>
          <div className='[&_button]:!h-9 [&_button]:!w-full [&_button]:whitespace-nowrap'>
            <DatePicker date={startDate} onChange={value => emit({ startDate: value?.toISOString() })} disablePastDates />
          </div>
        </div>
      </div>
    </div>
  )
}

export default RecurringScheduleFields
